import { SEO } from "../components/SEO";

export function TermsPage() {
  return (
    <>
      <SEO
        title="Terms of Service"
        description="Terms of Service for Valkyrie Robotics VEX Robotics coaching programs. Read our terms and conditions for participation in our educational robotics programs."
        canonical="/terms"
        keywords={[
          "terms of service",
          "Valkyrie Robotics terms",
          "VEX Robotics terms",
          "robotics coaching terms",
          "educational program terms",
          "robotics class terms",
        ]}
      />
      <div className="bg-neutral-900 min-h-screen">
        <div className="mx-auto max-w-4xl px-4 py-16">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">
            Terms of Service
          </h1>

          <div className="prose prose-invert prose-neutral prose-lg max-w-none [&_a]:text-brand-400 [&_a]:hover:text-brand-300 [&_a]:underline [&_a]:transition-colors [&_a]:duration-300">
            <div className="bg-neutral-800/30 rounded-2xl p-8 md:p-12 border border-neutral-700">
              <p className="text-gray-300 mb-6">
                <strong>Last updated:</strong>{" "}
                {new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-300 mb-6">
                By accessing and participating in programs offered by Valkyrie
                Robotics, LLC ("Company," "we," "us," or "our"), you accept and
                agree to be bound by the terms and provision of this agreement.
                If you do not agree to abide by the above, please do not use
                this service.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                2. Description of Service
              </h2>
              <p className="text-gray-300 mb-6">
                Valkyrie Robotics provides competitive VEX Robotics coaching and
                educational programs for students in Forsyth County, GA. Our
                services include VEX IQ and V5 robotics coaching, summer camps,
                and related educational activities.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                3. Program Registration and Payment
              </h2>
              <p className="text-gray-300 mb-6">
                Registration for our programs requires completion of enrollment
                forms and payment of applicable fees. Payment is due at the time
                of registration unless otherwise specified. We accept payments
                via methods listed in our program materials, which may include
                Zelle for Business. Refunds and cancellation policies are
                outlined in individual program materials.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                4. Student Conduct and Safety
              </h2>
              <p className="text-gray-300 mb-6">
                Students are expected to behave respectfully and safely during
                all program activities. The Company reserves the right to
                dismiss any student whose behavior is disruptive or unsafe.
                Parents/guardians will be notified of any behavioral issues.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                5. Security Monitoring
              </h2>
              <p className="text-gray-300 mb-6">
                For the safety and security of all participants, our facility is
                monitored by video surveillance for security purposes.
                Recordings are stored securely and may be reviewed in the event
                of an incident. Video surveillance is not used in restrooms or
                other private areas. With prior written parental/guardian
                consent, certain footage or still images may be used for
                marketing or promotional purposes.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                6. Liability and Assumption of Risk
              </h2>
              <p className="text-gray-300 mb-6">
                Participation in robotics activities involves inherent risks. By
                participating, students and parents/guardians acknowledge these
                risks and agree to hold Valkyrie Robotics harmless from any
                injuries or damages that may occur during program activities,
                except in cases of gross negligence. This includes, without
                limitation, risks of physical injury, illness, property damage,
                and other harm.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                7. Intellectual Property
              </h2>
              <p className="text-gray-300 mb-6">
                All program materials, curriculum, and intellectual property
                remain the property of Valkyrie Robotics. Students may use these
                materials for educational purposes but may not reproduce or
                distribute them without permission. Photographs, videos, and
                recordings of students during program activities, including
                those captured by our security cameras, may be used for
                marketing and promotional purposes only with prior written
                parental/guardian consent.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                8. Privacy and Data Protection
              </h2>
              <p className="text-gray-300 mb-6">
                We are committed to protecting your privacy. Personal
                information collected during registration and program
                participation is used solely for program administration and
                communication. Please refer to our Privacy Policy for detailed
                information about data handling.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                9. Program Modifications
              </h2>
              <p className="text-gray-300 mb-6">
                Valkyrie Robotics reserves the right to modify program
                schedules, content, or pricing with reasonable notice. We will
                make every effort to maintain program quality and continuity. In
                the event of emergencies such as inclement weather, facility
                issues, public health concerns, or other events beyond our
                control, we reserve the right to reschedule, modify, or cancel
                activities.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                10. Termination
              </h2>
              <p className="text-gray-300 mb-6">
                The Company may terminate participation in programs for
                violations of these terms or for other reasonable causes.
                Students and parents/guardians may withdraw from programs
                according to our cancellation policies. If a student is
                dismissed from a program due to conduct violations, no refund
                will be issued. If the Company cancels a program for reasons
                other than participant conduct, a full refund or credit toward a
                future program will be offered.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                11. Governing Law
              </h2>
              <p className="text-gray-300 mb-6">
                These terms shall be governed by and construed in accordance
                with the laws of the State of Georgia. Any disputes arising from
                these terms or program participation shall be resolved in the
                courts of Forsyth County, Georgia.
              </p>

              <h2 className="text-2xl font-semibold text-white mb-4">
                12. Contact Information
              </h2>
              <p className="text-gray-300 mb-6">
                For questions about these terms or our programs, please contact
                us at:
              </p>
              <div className="bg-neutral-700/50 rounded-lg p-4 mb-6">
                <p className="text-gray-300 mb-2">
                  <strong>Valkyrie Robotics, LLC</strong>
                  <br />
                  Email:{" "}
                  <a href="mailto:contact@valkyrie-robotics.com">
                    contact@valkyrie-robotics.com
                  </a>
                  <br />
                  Phone: <a href="tel:+1-470-239-0787">+1-470-239-0787</a>
                </p>
              </div>

              <div className="border-t border-neutral-600 pt-6 mt-8">
                <p className="text-sm text-gray-400">
                  These terms are subject to change. Please review them
                  periodically for updates. Continued participation in our
                  programs constitutes acceptance of any modified terms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
